// src/components/CrearClienteModal.js
import React, { useState } from "react";
import supabase from "../supabaseClient";
import Swal from "sweetalert2";
import "../estilos/ModalesEstilo.css";

/**
 * Props:
 * - onClienteCreado(cliente): se llama al crear un cliente nuevo
 * - onClienteActualizado(cliente): 🆕 se llama al guardar cambios de un cliente existente
 * - onClose: cierra el modal
 * - clienteExistente: 🆕 si llega un cliente con id, el modal entra en modo edición
 */
const CrearClienteModal = ({
  onClienteCreado,
  onClienteActualizado,
  onClose,
  clienteExistente = null,
}) => {
  const modoEdicion = !!clienteExistente?.id;

  const [form, setForm] = useState({
    nombre: clienteExistente?.nombre || "",
    identificacion: clienteExistente?.identificacion || "",
    telefono: clienteExistente?.telefono || "",
    direccion: clienteExistente?.direccion || "",
    email: clienteExistente?.email || ""
  });
  const [guardando, setGuardando] = useState(false);

  const guardarCliente = async () => {
    const { nombre, identificacion, telefono, direccion, email } = form;

    if (!nombre.trim()) {
      return Swal.fire("Campo requerido", "El nombre del cliente es obligatorio.", "warning");
    }

    setGuardando(true);

    const datosCliente = {
      nombre: nombre.trim(),
      identificacion: identificacion?.trim() || null,
      telefono: telefono?.trim() || null,
      direccion: direccion?.trim() || null,
      email: email?.trim() || null
    };

    // Verificar identificación duplicada.
    // 🆕 En edición hay que excluir al propio cliente o se marca a sí mismo como duplicado.
    if (datosCliente.identificacion) {
      let consulta = supabase
        .from("clientes")
        .select("id")
        .eq("identificacion", datosCliente.identificacion);

      if (modoEdicion) consulta = consulta.neq("id", clienteExistente.id);

      const { data: existentes, error: errorExistentes } = await consulta;

      if (errorExistentes) {
        console.error("Error buscando duplicados:", errorExistentes);
        setGuardando(false);
        return Swal.fire("Error", "Problema verificando cliente existente.", "error");
      }

      if (existentes && existentes.length > 0) {
        setGuardando(false);
        return Swal.fire("Ya existe", "Ya hay otro cliente con esa identificación.", "warning");
      }
    }

    // ===================== MODO EDICIÓN =====================
    if (modoEdicion) {
      const { data, error } = await supabase
        .from("clientes")
        .update(datosCliente)
        .eq("id", clienteExistente.id)
        .select();

      setGuardando(false);

      if (error) {
        console.error("❌ Error al actualizar cliente:", error);
        return Swal.fire("Error", "No se pudo actualizar el cliente.", "error");
      }

      // Si RLS no devuelve la fila, reconstruimos el objeto con lo que ya sabemos
      const actualizado = data?.[0] || { ...clienteExistente, ...datosCliente };

      Swal.fire({
        icon: "success",
        title: "Cliente actualizado",
        timer: 1500,
        showConfirmButton: false
      });

      if (typeof onClienteActualizado === "function") onClienteActualizado(actualizado);
      onClose();
      return;
    }

    // ===================== MODO CREACIÓN =====================
    const { data: clientesExistentes } = await supabase
      .from("clientes")
      .select("codigo");

    const codigos = (clientesExistentes || [])
      .map(c => c.codigo)
      .filter(Boolean)
      .filter(c => c.startsWith("C") && !isNaN(parseInt(c.slice(1))))
      .map(c => parseInt(c.slice(1)));

    const siguiente = Math.max(...codigos, 1000) + 1;

    const { data, error } = await supabase
      .from("clientes")
      .insert([{ ...datosCliente, codigo: `C${siguiente}` }])
      .select();

    setGuardando(false);

    if (!error && data?.length) {
      Swal.fire("Cliente creado", "El cliente fue guardado correctamente", "success");
      onClienteCreado(data[0]);
      onClose();
    } else {
      console.error("Error al guardar:", error);
      Swal.fire("Error", "No se pudo guardar el cliente", "error");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-contenedor ancho-medio" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={`modal-header ${modoEdicion ? "header-azul" : "header-verde"}`}>
          <h2>{modoEdicion ? "✏️ Editar Cliente" : "👤 Nuevo Cliente"}</h2>
          <button className="btn-cerrar-modal" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">

          {/* 🆕 Aviso de alcance (solo en edición) */}
          {modoEdicion && (
            <div
              style={{
                fontSize: 12,
                color: "#1e40af",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 16,
                lineHeight: 1.45
              }}
            >
              Estos datos se actualizan en la ficha del cliente, no solo en este pedido.
              {clienteExistente?.codigo ? ` · Código: ${clienteExistente.codigo}` : ""}
            </div>
          )}

          {/* Nombre - Campo principal */}
          <div className="modal-seccion">
            <label className="modal-label">Nombre completo: <span style={{ color: "#dc2626" }}>*</span></label>
            <input
              type="text"
              placeholder="Nombre del cliente"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="modal-input"
              autoFocus
            />
          </div>

          {/* Identificación y Teléfono */}
          <div className="modal-seccion">
            <div className="form-grid form-grid-2">
              <div>
                <label className="modal-label">Identificación:</label>
                <input
                  type="text"
                  placeholder="Documento"
                  value={form.identificacion}
                  onChange={(e) => setForm({ ...form, identificacion: e.target.value })}
                  className="modal-input"
                />
              </div>
              <div>
                <label className="modal-label">Teléfono:</label>
                <input
                  type="text"
                  placeholder="Número de contacto"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="modal-input"
                />
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="modal-seccion">
            <label className="modal-label">Dirección:</label>
            <input
              type="text"
              placeholder="Dirección del cliente"
              value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              className="modal-input"
            />
          </div>

          {/* Email */}
          <div className="modal-seccion">
            <label className="modal-label">Correo electrónico:</label>
            <input
              type="email"
              placeholder="ejemplo@correo.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="modal-input"
            />
          </div>

        </div>

        {/* Footer */}
        <div className="modal-footer footer-espaciado">
          <button onClick={onClose} className="btn-modal btn-secundario">
            Cancelar
          </button>
          <button
            onClick={guardarCliente}
            className={`btn-modal ${modoEdicion ? "btn-primario" : "btn-verde"}`}
            disabled={guardando}
          >
            {guardando ? "⏳ Guardando..." : modoEdicion ? "💾 Guardar cambios" : "💾 Guardar Cliente"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrearClienteModal;